import React, { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Heading, Spinner, Alert, AlertIcon, Button, HStack } from '@chakra-ui/react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useAuth } from '../../utils/AuthContext';
import { useToast } from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import * as XLSX from 'xlsx';

const AllUsersMarksheet = () => {
  const { auth } = useAuth();
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const toast = useToast();
  const gridRef = useRef();

  const paginationNumberFormatter = useCallback((params) => {
    return "[" + params.value.toLocaleString() + "]";
  }, []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: "agTextColumnFilter",
    floatingFilter: true,
    cellClass: "no-border",
    resizable: true,
  }), []);

  const columnDefs = useMemo(() => [
    { headerName: 'Ref ID', field: 'user.refid', filter: 'agTextColumnFilter', sortable: true, resizable: true },
    { headerName: 'Name', field: 'user.name', filter: 'agTextColumnFilter', sortable: true, resizable: true },
    { headerName: 'Emp Code', field: 'user.empcode', filter: 'agTextColumnFilter', sortable: true, resizable: true },
    { headerName: 'DOJ', field: 'user.doj', valueFormatter: ({ value }) => new Date(value).toLocaleDateString(), filter: 'agDateColumnFilter', sortable: true, resizable: true },
    { headerName: 'Designation', field: 'user.designation', filter: 'agTextColumnFilter', sortable: true, resizable: true },
    { headerName: 'Office', field: 'user.office', filter: 'agTextColumnFilter', sortable: true, resizable: true },
    { headerName: 'City', field: 'user.city', filter: 'agTextColumnFilter', sortable: true, resizable: true },
    { headerName: 'Email', field: 'user.email', filter: 'agTextColumnFilter', sortable: true, resizable: true },
    { headerName: 'Test Set', field: 'examAssignment.paperSetTitle', filter: 'agTextColumnFilter', sortable: true, resizable: true },
    { headerName: 'Exam Date', field: 'examAssignment.examDate', valueFormatter: ({ value }) => new Date(value).toLocaleDateString(), filter: 'agDateColumnFilter', sortable: true, resizable: true },
    { headerName: 'Is Completed', field: 'examAssignment.isCompleted', valueFormatter: ({ value }) => (value ? 'Yes' : 'No'), filter: 'agTextColumnFilter', sortable: true, resizable: true },
    { headerName: 'Submission Date', field: 'examAssignment.submissionDate', valueFormatter: ({ value }) => value ? new Date(value).toLocaleDateString() : 'N/A', filter: 'agDateColumnFilter', sortable: true, resizable: true },
    { headerName: 'Correct Answers', field: 'examAssignment.examDetails[0].correctAnswers', valueFormatter: params => params.data.examAssignment.examDetails.$values[0]?.correctAnswers || 'N/A', filter: 'agNumberColumnFilter', sortable: true, resizable: true },
    { headerName: 'Wrong Answers', field: 'examAssignment.examDetails[0].wrongAnswers', valueFormatter: params => params.data.examAssignment.examDetails.$values[0]?.wrongAnswers || 'N/A', filter: 'agNumberColumnFilter', sortable: true, resizable: true },
    { headerName: 'Correct Answer Marks', field: 'examAssignment.examDetails[0].correctAnswerMarks', valueFormatter: params => params.data.examAssignment.examDetails.$values[0]?.correctAnswerMarks || 'N/A', filter: 'agNumberColumnFilter', sortable: true, resizable: true },
    { headerName: 'Negative Marks', field: 'examAssignment.examDetails[0].negativeMarks', valueFormatter: params => params.data.examAssignment.examDetails.$values[0]?.negativeMarks || 'N/A', filter: 'agNumberColumnFilter', sortable: true, resizable: true },
    { headerName: 'Total Marks', field: 'examAssignment.examDetails[0].totalMarks', valueFormatter: params => params.data.examAssignment.examDetails.$values[0]?.totalMarks || 'N/A', filter: 'agNumberColumnFilter', sortable: true, resizable: true },
    { headerName: 'Not Attempted Questions', field: 'examAssignment.examDetails[0].notAttemptedQuestions', valueFormatter: params => params.data.examAssignment.examDetails.$values[0]?.notAttemptedQuestions || 'N/A', filter: 'agNumberColumnFilter', sortable: true, resizable: true },
    { headerName: 'Response Acceptable', field: 'examAssignment.examDetails[0].responseAcceptable', valueFormatter: params => params.data.examAssignment.examDetails.$values[0]?.responseAcceptable || 'N/A', filter: 'agTextColumnFilter', sortable: true, resizable: true },
    { headerName: 'Remarks', field: 'examAssignment.examDetails[0].remarks', valueFormatter: params => params.data.examAssignment.examDetails.$values[0]?.remarks || 'N/A', filter: 'agTextColumnFilter', sortable: true, resizable: true }
  ], []);

  const fetchMarksheet = async (startDate, endDate) => {
    try {
      let url = `${process.env.REACT_APP_API_URL}/api/Results/marksheetrange`;
      if (startDate && endDate) {
        url += `?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      setRowData(response.data.$values || response.data);
    } catch (error) {
      setError('Error fetching marksheet');
      console.error('Error fetching marksheet:', error);
      toast({
        title: "Error fetching marksheet",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarksheet();
  }, [auth.token, toast]);

  const flattenData = (data) => {
    return data.map((row) => {
      return {
        'Ref ID': row.user.refid,
        'Name': row.user.name,
        'Emp Code': row.user.empcode,
        'DOJ': new Date(row.user.doj).toLocaleDateString(),
        'Designation': row.user.designation,
        'Office': row.user.office,
        'City': row.user.city,
        'Email': row.user.email,
        'Test Set': row.examAssignment.paperSetTitle,
        'Exam Date': new Date(row.examAssignment.examDate).toLocaleDateString(),
        'Is Completed': row.examAssignment.isCompleted ? 'Yes' : 'No',
        'Submission Date': row.examAssignment.submissionDate ? new Date(row.examAssignment.submissionDate).toLocaleDateString() : 'N/A',
        'Correct Answers': row.examAssignment.examDetails.$values[0]?.correctAnswers || 'N/A',
        'Wrong Answers': row.examAssignment.examDetails.$values[0]?.wrongAnswers || 'N/A',
        'Correct Answer Marks': row.examAssignment.examDetails.$values[0]?.correctAnswerMarks || 'N/A',
        'Negative Marks': row.examAssignment.examDetails.$values[0]?.negativeMarks || 'N/A',
        'Total Marks': row.examAssignment.examDetails.$values[0]?.totalMarks || 'N/A',
        'Not Attempted Questions': row.examAssignment.examDetails.$values[0]?.notAttemptedQuestions || 'N/A',
        'Response Acceptable': row.examAssignment.examDetails.$values[0]?.responseAcceptable || 'N/A',
        'Remarks': row.examAssignment.examDetails.$values[0]?.remarks || 'N/A'
      };
    });
  };

  const onBtExport = () => {
    const exportData = flattenData(rowData);
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Marksheet');
    XLSX.writeFile(workbook, 'marksheet.xlsx');
  };

  const onGridReady = (params) => {
    gridRef.current = params.api;
  };

  const filterResultsByDate = () => {
    const startDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
    const endDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);
    fetchMarksheet(startDate, endDate);
  };

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Heading mb={4}>All Users' Marksheet</Heading>
      <HStack spacing={4} mb={4}>
        <Box>
          <label>Select Month:</label>
          <DatePicker
            selected={selectedMonth}
            onChange={(date) => setSelectedMonth(date)}
            dateFormat="MMM yyyy"
            showMonthYearPicker
            className="mb-4 highlight-datepicker"
          />
        </Box>
        <Button colorScheme="blue" onClick={filterResultsByDate}>
          Search
        </Button>
      </HStack>
      <Button onClick={onBtExport} colorScheme="teal" mb={4}>Export to Excel</Button>
      <div className="ag-theme-alpine" style={{ height: '80vh', width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{ filter: true, sortable: true, resizable: true, floatingFilter: true }}
          pagination={true}
          paginationPageSize={10}
          paginationNumberFormatter={paginationNumberFormatter}
          onGridReady={onGridReady}
        />
      </div>
    </Box>
  );
};

export default AllUsersMarksheet;
